// AdsTable.tsx
import { Table, Tag, Button, Modal, Image } from 'antd';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { BiTrash } from 'react-icons/bi';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import dayjs from 'dayjs';
import './Table.css';

interface AdsTableProps {
  data: any[];
  loading?: boolean;
  onEdit: (record: any) => void;
  onDelete: (record: any) => void;
}

const AdsTable = ({ data, loading, onEdit, onDelete }: AdsTableProps) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<any>(null);

  const handlePreview = (media: any[]) => {
    setPreviewMedia(media);
    setPreviewVisible(true);
  };

  const MediaPreviewModal = ({ media, visible, onClose }: any) => (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className="media-preview-modal"
    >
      <div className="media-preview-grid">
        {media?.map((item: any, index: number) => (
          <div key={index} className="media-preview-item">
            {item.type === 'image' ? (
              <Image
                src={item.url}
                alt="Ad Preview"
                className="preview-image"
              />
            ) : (
              <video
                src={item.url}
                controls
                className="preview-video"
              />
            )}
          </div>
        ))}
      </div>
    </Modal>
  );

  const columns: ColumnsType<any> = [
    {
      title: 'Institution',
      dataIndex: 'institutionName',
      key: 'institutionName',
      render: (text: string) => (
        <div className="institution-name">{text}</div>
      )
    },
    {
      title: 'Campaign Duration',
      key: 'duration',
      render: (text: string, record: any) => {
        // Convert Firestore timestamp to milliseconds and then to Date
        const startDate = record.startDate?._seconds ? 
          new Date(record.startDate._seconds * 1000) : 
          new Date();
        
        const endDate = record.endDate?._seconds ? 
          new Date(record.endDate._seconds * 1000) : 
          new Date();
    
        return (
          <div className="duration-cell">
            <div>{dayjs(startDate).format('DD/MM/YYYY')}</div>
            <div>to</div>
            <div>{dayjs(endDate).format('DD/MM/YYYY')}</div>
          </div>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Media',
      key: 'mediaFiles',
      render: (text: string, record: any) => (
        <div className="media-cell">
          <span>{record.mediaFiles.length} files</span>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record.mediaFiles)}
          >
            Preview
          </Button>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text: string, record: any) => (
        <div className="row">
            <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
            className="edit-button"
            style={{backgroundColor: '#4F5052', marginRight: '10px'}}
          >
          </Button>
          <Button 
          type="primary" 
          icon={<BiTrash />}
          onClick={() => onDelete(record)}
          className="edit-button"
          style={{backgroundColor: '#D32A2A'}}
        >
        </Button>
        </div>
      ),
    }
  ];

  return (
    <>
      <Table
        className="ads-table"
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} advertisements`
        }}
      />

      <MediaPreviewModal
        media={previewMedia}
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
      />
    </>
  );
};

export default AdsTable;